/**
 * Patient Management Shelf Component
 *
 * Expandable shelf that displays all demo patients and allows
 * canceling visits for selected or all patients.
 */

import { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CancelIcon from '@mui/icons-material/Cancel';
import PeopleIcon from '@mui/icons-material/People';

export interface DemoPatient {
  patientId: number;
  firstName: string;
  lastName: string;
  mrn: string;
  episodeCount: number;
  visitCount: number;
  activeVisitCount: number;
}

export interface PatientManagementShelfProps {
  patients: DemoPatient[];
  onCancelAllVisits: (patientIds: number[]) => Promise<void>;
  onViewPatient: (patientId: number) => void;
  loading?: boolean;
  disabled?: boolean;
}

export function PatientManagementShelf({
  patients,
  onCancelAllVisits,
  onViewPatient,
  loading = false,
  disabled = false,
}: PatientManagementShelfProps) {
  const [expanded, setExpanded] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState<Set<number>>(new Set());
  const [isCanceling, setIsCanceling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTogglePatient = (patientId: number) => {
    const newSelected = new Set(selectedPatients);
    if (newSelected.has(patientId)) {
      newSelected.delete(patientId);
    } else {
      newSelected.add(patientId);
    }
    setSelectedPatients(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPatients(new Set(patients.map((p) => p.patientId)));
    } else {
      setSelectedPatients(new Set());
    }
  };

  const handleCancelAll = async () => {
    setError(null);
    setIsCanceling(true);
    try {
      const allPatientIds = patients.map((p) => p.patientId);
      await onCancelAllVisits(allPatientIds);
      setSelectedPatients(new Set());
    } catch (err: any) {
      setError(err.message || 'Failed to cancel visits');
    } finally {
      setIsCanceling(false);
    }
  };

  const handleCancelSelected = async () => {
    if (selectedPatients.size === 0) return;

    setError(null);
    setIsCanceling(true);
    try {
      await onCancelAllVisits(Array.from(selectedPatients));
      setSelectedPatients(new Set());
    } catch (err: any) {
      setError(err.message || 'Failed to cancel visits');
    } finally {
      setIsCanceling(false);
    }
  };

  const allSelected = patients.length > 0 && selectedPatients.size === patients.length;
  const someSelected = selectedPatients.size > 0 && selectedPatients.size < patients.length;
  const totalActiveVisits = patients.reduce((sum, p) => sum + p.activeVisitCount, 0);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disabled={disabled}
      sx={{
        mt: 4,
        '&.Mui-disabled': {
          bgcolor: 'action.disabledBackground',
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
          <PeopleIcon />
          <Typography variant="h6">Demo Patient Management</Typography>
          {patients.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Chip
                label={`${patients.length} patient${patients.length !== 1 ? 's' : ''}`}
                size="small"
                color="primary"
              />
              <Chip
                label={`${totalActiveVisits} active visit${totalActiveVisits !== 1 ? 's' : ''}`}
                size="small"
                color={totalActiveVisits > 0 ? 'success' : 'default'}
              />
            </Box>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : patients.length === 0 ? (
          <Alert severity="info">
            No demo patients found. Execute a scenario to create demo data.
          </Alert>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={isCanceling}
                  />
                }
                label="Select All"
              />

              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelSelected}
                  disabled={selectedPatients.size === 0 || isCanceling}
                >
                  {isCanceling && selectedPatients.size > 0
                    ? 'Canceling...'
                    : `Cancel Selected (${selectedPatients.size})`}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelAll}
                  disabled={isCanceling}
                >
                  {isCanceling && selectedPatients.size === 0
                    ? 'Canceling...'
                    : 'Cancel All Visits'}
                </Button>
              </Box>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Select</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>MRN</TableCell>
                  <TableCell align="center">Episodes</TableCell>
                  <TableCell align="center">Total Visits</TableCell>
                  <TableCell align="center">Active Visits</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patients.map((patient) => (
                  <TableRow
                    key={patient.patientId}
                    hover
                    onClick={() => !isCanceling && handleTogglePatient(patient.patientId)}
                    sx={{ cursor: isCanceling ? 'default' : 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedPatients.has(patient.patientId)}
                        disabled={isCanceling}
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewPatient(patient.patientId);
                        }}
                        sx={{ textAlign: 'left' }}
                      >
                        {patient.firstName} {patient.lastName}
                      </Link>
                    </TableCell>
                    <TableCell>{patient.mrn}</TableCell>
                    <TableCell align="center">{patient.episodeCount}</TableCell>
                    <TableCell align="center">{patient.visitCount}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={patient.activeVisitCount}
                        size="small"
                        color={patient.activeVisitCount > 0 ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
